import React from "react";
import PropTypes from "prop-types";
import BaseBlock from "./BaseBlock";
import useBlockData from "./useBlockData";
import FlexTable from "../../../components/ui-components/FlexTable";
import StatusPill from "../../Budgets/components/StatusPill";
import { fmtEur, fmtEurSigned } from "./format";

// TONE_CLASS mirrors HorizonTableBlock's mapping of variance sign to a colour class: a
// budget running over (variance > 0) is adverse (red), one running under is favorable (green).
const TONE_CLASS = {
  adverse: "a4-kpi-tone-adverse",
  favorable: "a4-kpi-tone-favorable",
  neutral: "a4-kpi-tone-neutral",
};

function varianceTone(variance) {
  if (variance > 0) return "adverse";
  if (variance < 0) return "favorable";
  return "neutral";
}

// PILL_STATUS maps boardfigures budget-variance row status vocabulary ("over" | "watch" |
// "on_track" | "under") onto StatusPill's own STATUS_CONFIG keys (Budgets/components/
// StatusPill.jsx): "over" is a literal StatusPill key already; "watch" has no identically
// named entry so it maps onto "at_risk" ("At risk"); "on_track"/"under" both map onto
// "green" ("On track").
const PILL_STATUS = { over: "over", watch: "at_risk", on_track: "green", under: "green" };

function pillStatus(status) {
  return PILL_STATUS[status] || status;
}

// fmtPct renders the already-computed percentage number the backend sends (e.g. 20.5 means
// 20.5%, NOT a fraction) — do not route this through format.js's signedPct, which expects a
// fraction and multiplies by 100.
function fmtPct(v) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return `${v >= 0 ? "+" : ""}${Number(v).toFixed(1)}%`;
}

const columns = [
  { Header: "Budget", accessor: "name" },
  { Header: "Owner", accessor: "owner", Cell: ({ value }) => value || "—" },
  { Header: "Cap", accessor: "capCents", align: "right", Cell: ({ value }) => fmtEur(value / 100) },
  { Header: "Actual", accessor: "actualCents", align: "right", Cell: ({ value }) => fmtEur(value / 100) },
  { Header: "Forecast", accessor: "forecastCents", align: "right", Cell: ({ value }) => fmtEur(value / 100) },
  {
    Header: "Variance",
    accessor: "varianceCents",
    align: "right",
    Cell: ({ value }) => (
      <span className={TONE_CLASS[varianceTone(value)]}>{fmtEurSigned(value / 100)}</span>
    ),
  },
  {
    Header: "Variance %",
    accessor: "variancePct",
    align: "right",
    Cell: ({ value }) => (
      <span className={TONE_CLASS[varianceTone(value)]}>{fmtPct(value)}</span>
    ),
  },
  {
    Header: "Status",
    accessor: "status",
    align: "center",
    Cell: ({ value }) => (value ? <StatusPill status={pillStatus(value)} /> : "—"),
  },
];

/**
 * BudgetVarianceTableBlock - per-budget actual/forecast-vs-cap variance table, ending in a
 * portfolio total row (budget_variance_table block type).
 *
 * GET /api/v1/board-reports/{boardReportId}/runs/{runId}/figures/budget-variance -> envelope
 * key "budget_variance" -> { rows: [BudgetVarianceRow], total: BudgetVarianceRow } (backend
 * internal/boardfigures/budget_risk.go). The endpoint path has no "-table" suffix even
 * though this block's registry key does. `total` carries no budgetId/owner (the backend does
 * set its variance status), so it's appended as a trailing row keyed by name rather than id.
 */
const BudgetVarianceTableBlock = ({ title = "Budget variance", boardReportId, runId }) => {
  const { data, isLoading, error } = useBlockData(
    `/api/v1/board-reports/${boardReportId}/runs/${runId}/figures/budget-variance`
  );

  const rows = data?.budget_variance?.rows ?? [];
  const total = data?.budget_variance?.total;
  const tableRows = total ? [...rows, total] : rows;

  return (
    <BaseBlock title={title} isLoading={isLoading} error={error} className="a4-budget-variance-table">
      <FlexTable
        columns={columns}
        data={tableRows}
        getRowId={(row) => row.budgetId ?? row.name}
      />
    </BaseBlock>
  );
};

BudgetVarianceTableBlock.propTypes = {
  title: PropTypes.string,
  boardReportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  runId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default BudgetVarianceTableBlock;
